import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock, Send, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSubmitted(true);
      toast.success(t("contact.form.success"));
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO title="Contact Us" description="Contact Euro Cars Rent in Thessaloniki. Phone: +30 690 908 6661. Located in Pylaea. Mon-Fri 09:00-21:00." />
      <section className="py-12 md:py-20 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h1 data-testid="contact-title" className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight mb-3">
              {t("contact.title")}
            </h1>
            <p className="font-dm-sans text-base md:text-lg text-slate-500">{t("contact.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                {submitted ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-outfit font-semibold text-xl text-slate-900 mb-2">{t("contact.form.success")}</h3>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("contact.form.name")} *</label>
                        <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 bg-slate-50 border-slate-200 rounded-lg" required />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("contact.form.email")} *</label>
                        <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 bg-slate-50 border-slate-200 rounded-lg" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("contact.form.phone")}</label>
                        <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 bg-slate-50 border-slate-200 rounded-lg" />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("contact.form.subject")}</label>
                        <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 bg-slate-50 border-slate-200 rounded-lg" />
                      </div>
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">{t("contact.form.message")} *</label>
                      <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-50 border-slate-200 rounded-lg" rows={5} required />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={submitting}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-outfit font-medium rounded-full px-8 py-6 shadow-lg hover:shadow-orange-500/25 transition-all"
                    >
                      <Send className="w-4 h-4 mr-2" /> {submitting ? "..." : t("contact.form.send")}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-medium text-sm text-slate-900">Address</h4>
                      <p className="font-dm-sans text-sm text-slate-500">{t("contact.info.address")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center shrink-0">
                      <Phone className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-medium text-sm text-slate-900">Phone</h4>
                      <a href="tel:+306909086661" className="font-dm-sans text-sm text-orange-500 hover:underline">{t("contact.info.phone")}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-medium text-sm text-slate-900">Email</h4>
                      <a href="mailto:info@eurocars-rent.gr" className="font-dm-sans text-sm text-orange-500 hover:underline">{t("contact.info.email")}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-medium text-sm text-slate-900">Hours</h4>
                      <p className="font-dm-sans text-sm text-slate-500">{t("contact.info.hours")}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                <iframe
                  title="Euro Cars Location - Tompazi 49, Pilea"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d761.9!2d22.9876!3d40.5847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a839dc8d51f8ab%3A0x7e5c6d7b8c9a0b1c!2sTompazi%2049%2C%20Pilea%20555%2035%2C%20Greece!5e0!3m2!1sen!2sgr!4v1704067200000"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a 
                  href="https://maps.app.goo.gl/p3tnpQXy4VjHKYpT6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 bg-slate-50 hover:bg-slate-100 transition-colors font-dm-sans text-sm text-slate-600"
                >
                  <MapPin className="w-4 h-4" /> Open in Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
